import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { TransactionButton } from 'components/Button/Button'
import { InfoBoxCol } from 'components/InfoBox'
import RoundedSelect from 'components/RoundedSelect'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { useTimer } from 'hooks/useTimer'
import { Circle, StatusNode } from 'sections/shared/Layout/AppLayout/Header/OneClickTradingActions'
import SelectTradingType from 'sections/shared/components/SelectTradingType'
import { setOpenModal } from 'state/app/reducer'
import { closeOneClickSession, startOneClickSession } from 'state/futures/actions'
import { selectTradingMode } from 'state/futures/common/selectors'
import { setPreferences } from 'state/futures/reducer'
import { selectAbstractionDelegated, selectSubmittingFuturesTx } from 'state/futures/selectors'
import { TradingModes } from 'state/futures/types'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectIsSubAccountMode } from 'state/wallet/selectors'
import media from 'styles/media'
import { SECOND_INTERVALS, convertSecondsToInterval } from 'utils/dates'

import { EVENT_NAME } from 'constants/matomoEvents'
import { useAnalytics } from 'hooks/useAnalytics'
import useAbstractionInfo from '../hooks/useAbstractionInfo'
import { useWithdrawal } from '../hooks/useWithdrawal'

const SessionManagement = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { sessionInfo, isSessionActive } = useAbstractionInfo()
	const { trackOneClickTrading } = useAnalytics()
	const { balance, withdraw, withdrawalText } = useWithdrawal()
	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const txProcessing = useAppSelector(selectSubmittingFuturesTx)
	const disabled = useMemo(() => isSubAccountMode || txProcessing, [isSubAccountMode, txProcessing])

	const [interval, setInterval] = useState(86400)

	const timer = useTimer(sessionInfo?.validUntil ?? 0)

	const activateSession = useCallback(() => {
		trackOneClickTrading(EVENT_NAME.SUBMIT_ACTIVATE_1CT)
		dispatch(startOneClickSession(interval))
	}, [dispatch, interval, trackOneClickTrading])

	const deactivateSession = useCallback(() => {
		trackOneClickTrading(EVENT_NAME.CLICK_SIGN_EVERY_TXN)
		dispatch(closeOneClickSession())
	}, [dispatch, trackOneClickTrading])

	const sessionDuration = useMemo(() => {
		if (!sessionInfo) return

		const interval = sessionInfo.validUntil - sessionInfo.validAfter
		return convertSecondsToInterval(interval)
	}, [sessionInfo])

	return (
		<>
			<SessionManagementTitle>
				{isSessionActive
					? t('futures.one-click-trading.session')
					: t('futures.one-click-trading.no-session')}
			</SessionManagementTitle>

			<SessionManagementSection>
				<InfoBoxCol
					title={t('futures.one-click-trading.status.title')}
					nodeValue={
						<StatusNode>
							<Circle status={isSessionActive} />
							<span>
								{isSessionActive
									? t('futures.one-click-trading.status.active')
									: t('futures.one-click-trading.status.inactive')}
							</span>
						</StatusNode>
					}
				/>
				<InfoBoxCol
					title={t('futures.one-click-trading.duration')}
					nodeValue={
						sessionDuration ? (
							<StatusNode>{sessionDuration}</StatusNode>
						) : (
							<RoundedSelect
								values={SECOND_INTERVALS}
								onChange={(v) => setInterval(v)}
								selectedValue={interval}
								defaultLabel="1D"
								isModal={true}
							/>
						)
					}
				/>
				<InfoBoxCol
					title={t('futures.one-click-trading.expires-in')}
					textValue={isSessionActive ? timer : '–'}
				/>
			</SessionManagementSection>
			<Spacer height={20} />
			<StyledButton
				noOutline
				onClick={isSessionActive ? deactivateSession : activateSession}
				disabled={disabled}
				loading={txProcessing}
			>
				{isSessionActive
					? t('futures.one-click-trading.disable')
					: t('futures.one-click-trading.enable')}
			</StyledButton>

			{!!balance && (
				<>
					<Spacer height={20} />
					<StyledButton noOutline onClick={withdraw} loading={txProcessing}>
						{withdrawalText}
					</StyledButton>
				</>
			)}
		</>
	)
})

const ManageTab = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const { trackOneClickTrading } = useAnalytics()
	const isSubAccountMode = useAppSelector(selectIsSubAccountMode)
	const isDelegated = useAppSelector(selectAbstractionDelegated)
	const tradingMode = useAppSelector(selectTradingMode)

	const openOnboard = useCallback(() => {
		if (tradingMode === TradingModes.DEFAULT) {
			dispatch(setOpenModal(null))
			return
		}
		trackOneClickTrading(EVENT_NAME.CLICK_SETUP_1CT)
		dispatch(setPreferences({ tradingMode: TradingModes.ONE_CLICK }))
		dispatch(setOpenModal('one_click_trading_onboard'))
	}, [dispatch, tradingMode, trackOneClickTrading])

	return isDelegated ? (
		<SessionManagement />
	) : (
		<>
			<SelectTradingType />
			<Spacer height={20} />
			<StyledButton noOutline onClick={openOnboard} disabled={isSubAccountMode}>
				{tradingMode === TradingModes.ONE_CLICK
					? t('futures.one-click-trading.onboard.activate')
					: t('futures.modals.trading-mode.default-title')}
			</StyledButton>
		</>
	)
})

const SessionManagementSection = styled.div`
	border: ${(props) => props.theme.colors.selectedTheme.border};
	border-radius: 4px;
	padding: 15px;

	display: grid;
	grid-template-columns: repeat(3, 1fr);

	${media.lessThan('md')`
		grid-template-columns: repeat(2, 1fr);
	`}

	gap: 20px;
`

const SessionManagementTitle = styled(Body)`
	margin-bottom: 20px;
	font-size: 13px;
`

const StyledButton = styled(TransactionButton)`
	height: 50px;
	width: 100%;
`

export default ManageTab
